import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Settings, Lock, UserPlus, Trash2, Shield, ShieldCheck } from "lucide-react";
import { useAuth } from "@/App";
import ScheduleSettings from "@/components/admin/ScheduleSettings";
import api from "@/lib/api";

export default function AdminSettings() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";

  // Password
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState("");

  // Users
  const [admins, setAdmins] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: "", name: "", password: "", role: "admin" });
  const [addLoading, setAddLoading] = useState(false);
  const [addMsg, setAddMsg] = useState("");

  useEffect(() => {
    if (isSuperadmin) {
      api.get("/admin/users").then(r => setAdmins(r.data)).catch(() => {});
    }
  }, [isSuperadmin]);

  const changePassword = async () => {
    setPwMsg("");
    if (pw.new_password !== pw.confirm) { setPwMsg("Пароли не совпадают"); return; }
    if (pw.new_password.length < 6) { setPwMsg("Минимум 6 символов"); return; }
    setPwLoading(true);
    try {
      await api.put("/admin/password", { current_password: pw.current_password, new_password: pw.new_password });
      setPwMsg("Пароль успешно изменён");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      setPwMsg(e.response?.data?.detail || "Ошибка");
    }
    setPwLoading(false);
  };

  const addAdmin = async () => {
    setAddMsg("");
    if (!newAdmin.email || !newAdmin.name || !newAdmin.password) { setAddMsg("Заполните все поля"); return; }
    if (newAdmin.password.length < 6) { setAddMsg("Пароль: минимум 6 символов"); return; }
    setAddLoading(true);
    try {
      await api.post("/admin/users", newAdmin);
      setAddOpen(false);
      setNewAdmin({ email: "", name: "", password: "", role: "admin" });
      const r = await api.get("/admin/users");
      setAdmins(r.data);
    } catch (e) {
      setAddMsg(e.response?.data?.detail || "Ошибка");
    }
    setAddLoading(false);
  };

  const deleteAdmin = async (id, email) => {
    if (!window.confirm(`Удалить администратора ${email}?`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      setAdmins(admins.filter(a => a.id !== id));
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка");
    }
  };

  return (
    <div data-testid="admin-settings">
      <h1 className="font-serif text-3xl flex items-center gap-3 mb-8">
        <Settings className="w-8 h-8 text-primary" />
        Настройки
      </h1>

      {/* Working schedule */}
      <ScheduleSettings />

      {/* Change Password */}
      <div className="bg-card border border-border p-6 mb-6 rounded-sm max-w-lg">
        <h2 className="font-sans font-semibold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
          <Lock className="w-4 h-4 text-primary" /> Изменить пароль
        </h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Текущий пароль</label>
            <Input
              type="password"
              value={pw.current_password}
              onChange={e => setPw({ ...pw, current_password: e.target.value })}
              className="bg-background border-border rounded-none"
              data-testid="current-password-input"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Новый пароль</label>
            <Input
              type="password"
              value={pw.new_password}
              onChange={e => setPw({ ...pw, new_password: e.target.value })}
              className="bg-background border-border rounded-none"
              data-testid="new-password-input"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Подтвердите новый пароль</label>
            <Input
              type="password"
              value={pw.confirm}
              onChange={e => setPw({ ...pw, confirm: e.target.value })}
              className="bg-background border-border rounded-none"
              data-testid="confirm-password-input"
            />
          </div>
          {pwMsg && (
            <p className={`text-sm ${pwMsg.includes("успешно") ? "text-green-600" : "text-red-500"}`} data-testid="password-message">
              {pwMsg}
            </p>
          )}
          <Button
            onClick={changePassword}
            disabled={pwLoading}
            className="bg-primary text-primary-foreground rounded-none"
            data-testid="change-password-btn"
          >
            {pwLoading ? "..." : "Сохранить пароль"}
          </Button>
        </div>
      </div>

      {/* Admin Users - only for superadmin */}
      {isSuperadmin && (
        <div className="bg-card border border-border p-6 rounded-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-sans font-semibold text-sm uppercase tracking-widest flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-primary" /> Администраторы
            </h2>
            <Button
              onClick={() => { setAddOpen(true); setAddMsg(""); }}
              className="bg-primary text-primary-foreground rounded-none gap-2 text-xs"
              data-testid="add-admin-btn"
            >
              <UserPlus className="w-3.5 h-3.5" /> Добавить
            </Button>
          </div>

          <div className="space-y-2" data-testid="admins-list">
            {admins.map(a => (
              <div key={a.id} className="flex items-center justify-between bg-background border border-border p-3 rounded-sm" data-testid={`admin-${a.id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${a.role === "superadmin" ? "bg-yellow-100 text-yellow-700" : "bg-primary/10 text-primary"}`}>
                    {a.role === "superadmin" ? <ShieldCheck className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{a.name}</p>
                    <p className="text-xs text-muted-foreground">{a.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] px-2 py-0.5 rounded-sm ${a.role === "superadmin" ? "bg-yellow-100 text-yellow-700" : "bg-primary/10 text-primary"}`}>
                    {a.role === "superadmin" ? "Суперадмин" : "Админ"}
                  </span>
                  {a.id !== user?.id && (
                    <button
                      onClick={() => deleteAdmin(a.id, a.email)}
                      className="text-red-400 hover:text-red-500 p-1"
                      data-testid={`delete-admin-${a.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Add Admin Dialog */}
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogContent className="bg-card border-border max-w-sm">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Новый администратор</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Имя</label>
                  <Input
                    value={newAdmin.name}
                    onChange={e => setNewAdmin({ ...newAdmin, name: e.target.value })}
                    className="bg-background border-border rounded-none"
                    placeholder="Имя Фамилия"
                    data-testid="new-admin-name"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Email</label>
                  <Input
                    type="email"
                    value={newAdmin.email}
                    onChange={e => setNewAdmin({ ...newAdmin, email: e.target.value })}
                    className="bg-background border-border rounded-none"
                    placeholder="admin@example.com"
                    data-testid="new-admin-email"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Пароль</label>
                  <Input
                    type="password"
                    value={newAdmin.password}
                    onChange={e => setNewAdmin({ ...newAdmin, password: e.target.value })}
                    className="bg-background border-border rounded-none"
                    placeholder="Минимум 6 символов"
                    data-testid="new-admin-password"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Роль</label>
                  <select
                    value={newAdmin.role}
                    onChange={e => setNewAdmin({ ...newAdmin, role: e.target.value })}
                    className="w-full h-9 bg-background border border-border px-3 text-sm"
                    data-testid="new-admin-role"
                  >
                    <option value="admin">Админ</option>
                    <option value="superadmin">Суперадмин</option>
                  </select>
                </div>
                {addMsg && <p className="text-sm text-red-500">{addMsg}</p>}
                <Button
                  onClick={addAdmin}
                  disabled={addLoading}
                  className="w-full bg-primary text-primary-foreground rounded-none"
                  data-testid="submit-new-admin-btn"
                >
                  {addLoading ? "..." : "Создать"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}
